import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Handle, NodeProps, NodeResizer, Position } from '@xyflow/react';

import { useViewMode } from 'state/ViewModeContext';
import { useStepRunColors } from 'state/StepRunContext';
import { useFormatMode } from 'state/FormatContext';
import { useValidation } from 'state/ValidationContext';
import { useWorkflowCallbacks } from 'state/WorkflowCallbacksContext';
import type { ActionContract } from 'model/actions';
import { formatTypeLabel } from 'model/actions';
import { formatName } from 'model/nameFormatting';
import type { WorkflowNode as WorkflowNodeT } from 'model/xyflowMapping';
import { SPECIAL_ACTIONS, isForEachAction } from 'model/specialActions';
import { getRunOverrideStyle } from 'utils/runColorStyle';
import { loadActions } from 'hooks/useActionsCatalog';
import { AddNextButton } from './AddNextButton';
import {
  StyledContractName,
  StyledDataflowColumn,
  StyledDataflowRow,
  StyledHandleLabel,
  StyledHandleRow,
  StyledNode,
  StyledOutputName,
  StyledOutputRow,
  StyledOutputType,
  StyledOutputsBlock,
  StyledOutputsHeading,
  StyledStepIdInput,
  StyledStepIdLabel,
  StyledSubLabel,
  StyledUnknownActionBanner,
  type NodeKind,
} from './WorkflowNode.styled';

const handleStyleLeft: React.CSSProperties = { left: -8, top: 20 };
const handleStyleRight: React.CSSProperties = { right: -8, top: 20 };
const fieldHandleStyleLeft: React.CSSProperties = { left: -8, top: '50%' };
const fieldHandleStyleRight: React.CSSProperties = { right: -8, top: '50%' };
const handleStyleLeftDataflow: React.CSSProperties = { left: -8, top: 24 };
const handleStyleRightDataflow: React.CSSProperties = { right: -8, top: 24 };

const WorkflowNodeInner = ({ id, data, selected }: NodeProps<WorkflowNodeT>) => {
  const [contract, setContract] = useState<ActionContract | null>(null);
  const viewMode = useViewMode();
  const stepRunColors = useStepRunColors();
  const formatMode = useFormatMode();
  const validation = useValidation();
  const { onRenameStep } = useWorkflowCallbacks();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const { actionId, stepId } = data;

  const nodeTypeFlags = useMemo(() => {
    const isInitial = actionId === SPECIAL_ACTIONS.initial;
    const isOutput = actionId === SPECIAL_ACTIONS.output;
    const isRoute = actionId === SPECIAL_ACTIONS.route;
    const isStartIteration = actionId === SPECIAL_ACTIONS.startIteration;
    const isEndIteration = actionId === SPECIAL_ACTIONS.endIteration;
    const isForEachNode = isForEachAction(actionId);
    const isForEachBoundary = isStartIteration || isEndIteration;
    const isSpecial = isInitial || isOutput || isRoute || isForEachNode || isForEachBoundary;
    return {
      isInitial,
      isOutput,
      isRoute,
      isStartIteration,
      isEndIteration,
      isForEach: isForEachNode,
      isForEachBoundary,
      isSpecial,
      // Hide target handle on initial and startIteration; hide source handle on output and endIteration.
      hideTarget: isInitial || isStartIteration,
      hideSource: isOutput || isEndIteration,
    };
  }, [actionId]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const actions = await loadActions();
        if (!isMounted) return;
        const match = actions.find((action) => action.actionId === actionId) ?? null;
        setContract(match);
      } catch {
        // Catalog fetch failed (e.g. offline); node falls back to its unknown-action rendering.
      }
    };
    void load();
    return () => {
      isMounted = false;
    };
  }, [actionId]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setEditValue(stepId);
      setIsEditing(true);
    },
    [stepId],
  );

  const confirmRename = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== stepId) {
      onRenameStep(stepId, trimmed);
    }
    setIsEditing(false);
  }, [editValue, stepId, onRenameStep]);

  const handleEditChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  }, []);

  const handleEditClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') confirmRename();
      if (e.key === 'Escape') setIsEditing(false);
    },
    [confirmRename],
  );

  const runColor = stepRunColors ? (stepRunColors[stepId] ?? null) : null;
  const runOverride = useMemo(() => getRunOverrideStyle(runColor), [runColor]);
  const isInvalid = validation.badNodeIds.has(id);
  const invalidTitle = useMemo(() => {
    if (!isInvalid) return undefined;
    const own = validation.issues.filter((i) => i.nodeId === id);
    if (own.length === 0) return undefined;
    return own.map((i) => `• ${i.message}`).join('\n');
  }, [isInvalid, validation.issues, id]);

  const kind: NodeKind = useMemo(() => {
    if (nodeTypeFlags.isRoute) return 'route';
    if (nodeTypeFlags.isForEach) return 'forEach';
    if (nodeTypeFlags.isForEachBoundary) return 'forEachBoundary';
    if (nodeTypeFlags.isInitial || nodeTypeFlags.isOutput) return 'normal';
    if (contract) return 'normal';
    return 'unknown';
  }, [
    nodeTypeFlags.isRoute,
    nodeTypeFlags.isForEach,
    nodeTypeFlags.isForEachBoundary,
    nodeTypeFlags.isInitial,
    nodeTypeFlags.isOutput,
    contract,
  ]);

  const stepIdElement = isEditing ? (
    <StyledStepIdInput
      autoFocus
      value={editValue}
      onChange={handleEditChange}
      onKeyDown={handleKeyDown}
      onBlur={confirmRename}
      onClick={handleEditClick}
      aria-label='Rename step'
    />
  ) : (
    <StyledStepIdLabel onDoubleClick={handleDoubleClick} title='Double-click to rename'>
      {stepId}
    </StyledStepIdLabel>
  );

  // REASON: dynamic per-step run colour comes from runtime job-status data
  // (StepRunContext) and cannot be expressed as a static theme token or
  // styled-component variant. Inline `style` prop is the appropriate shape.
  const styleOverride =
    runOverride && Object.keys(runOverride).length > 0 ? runOverride : undefined;
  // When a run color is active, that override owns the border and should not
  // get the invalid-error frame on top of it (matches the legacy behaviour
  // where `invalidStyle` was skipped if `runColor` was set).
  const invalidFlag = isInvalid && !runColor;

  const { hideTarget, hideSource, isForEach } = nodeTypeFlags;

  if (!contract) {
    // In Dataflow mode, data edges target `in:<fieldName>` handles derived from
    // the consumer's StepOutput bindings. Contract-less nodes (e.g. Output) need
    // matching anchor handles or React Flow logs orphan-edge errors.
    const dataflowInputHandles =
      viewMode === 'dataflow' && !hideTarget ? Object.keys(data.input ?? {}) : [];

    return (
      <StyledNode
        $kind={kind}
        $selected={Boolean(selected)}
        $invalid={invalidFlag}
        $isForEach={isForEach}
        style={styleOverride}
        title={invalidTitle}
      >
        {isForEach && <NodeResizer minWidth={200} minHeight={80} />}
        {!hideTarget && (
          <Handle type='target' position={Position.Left} id='in' style={handleStyleLeft} />
        )}
        {dataflowInputHandles.map((fieldName) => (
          <Handle
            key={fieldName}
            type='target'
            position={Position.Left}
            id={`in:${fieldName}`}
            style={fieldHandleStyleLeft}
          />
        ))}
        {stepIdElement}
        <StyledSubLabel>{formatName(actionId, formatMode)}</StyledSubLabel>
        {!nodeTypeFlags.isSpecial && (
          <StyledUnknownActionBanner>
            <span>&#9888;</span> Unknown action
          </StyledUnknownActionBanner>
        )}
        {!hideSource && (
          <Handle type='source' position={Position.Right} id='out' style={handleStyleRight} />
        )}
        {!hideSource && !isForEach && <AddNextButton stepId={stepId} />}
      </StyledNode>
    );
  }

  const inputs = contract.inputs ?? [];
  const outputs = contract.outputs ?? [];

  const nodeHeader = (
    <>
      {stepIdElement}
      <StyledContractName>{contract.displayName}</StyledContractName>
    </>
  );

  // Flow mode: simple single handles, plus a list of output names so users can
  // see what each step produces without switching to Dataflow.
  if (viewMode === 'flow') {
    return (
      <StyledNode
        $kind={kind}
        $selected={Boolean(selected)}
        $invalid={invalidFlag}
        $isForEach={isForEach}
        style={styleOverride}
        title={invalidTitle}
      >
        {isForEach && <NodeResizer minWidth={200} minHeight={80} />}
        {!hideTarget && (
          <Handle type='target' position={Position.Left} id='in' style={handleStyleLeft} />
        )}
        {nodeHeader}
        {!hideSource && outputs.length > 0 && (
          <StyledOutputsBlock>
            <StyledOutputsHeading>Outputs</StyledOutputsHeading>
            {outputs.map((o) => (
              <StyledOutputRow key={o.name}>
                <StyledOutputName>{formatName(o.name, formatMode)}</StyledOutputName>
                <StyledOutputType>{formatTypeLabel(o.type, o.isCollection)}</StyledOutputType>
              </StyledOutputRow>
            ))}
          </StyledOutputsBlock>
        )}
        {!hideSource && (
          <Handle type='source' position={Position.Right} id='out' style={handleStyleRight} />
        )}
        {!hideSource && !isForEach && <AddNextButton stepId={stepId} />}
      </StyledNode>
    );
  }

  // Dataflow mode: per-field port handles plus a generic `in`/`out` anchor
  // for control-flow edges (runAfter) so they resolve regardless of contract.
  return (
    <StyledNode
      $kind={kind}
      $selected={Boolean(selected)}
      $invalid={invalidFlag}
      $isForEach={isForEach}
      style={styleOverride}
      title={invalidTitle}
    >
      {isForEach && <NodeResizer minWidth={200} minHeight={80} />}
      {!hideTarget && (
        <Handle type='target' position={Position.Left} id='in' style={handleStyleLeftDataflow} />
      )}
      {!hideSource && (
        <Handle type='source' position={Position.Right} id='out' style={handleStyleRightDataflow} />
      )}
      {nodeHeader}
      <StyledDataflowRow>
        <StyledDataflowColumn>
          {!hideTarget &&
            inputs.map((input) => (
              <StyledHandleRow key={input.name}>
                <Handle
                  type='target'
                  position={Position.Left}
                  id={`in:${input.name}`}
                  style={fieldHandleStyleLeft}
                />
                <StyledHandleLabel>{formatName(input.name, formatMode)}</StyledHandleLabel>
              </StyledHandleRow>
            ))}
        </StyledDataflowColumn>
        <StyledDataflowColumn $align='right'>
          {!hideSource &&
            outputs.map((output) => (
              <StyledHandleRow key={output.name} $align='right'>
                <StyledHandleLabel>{formatName(output.name, formatMode)}</StyledHandleLabel>
                <Handle
                  type='source'
                  position={Position.Right}
                  id={`out:${output.name}`}
                  style={fieldHandleStyleRight}
                />
              </StyledHandleRow>
            ))}
        </StyledDataflowColumn>
      </StyledDataflowRow>
    </StyledNode>
  );
};

const MemoWorkflowNode = React.memo(WorkflowNodeInner);

export { MemoWorkflowNode as WorkflowNode };
export default MemoWorkflowNode;
